

// https://codesandbox.io/p/sandbox/plotly-js-heatmap-7fmizo?file=%2Fsrc%2FApp.js

import numeral from "numeral";
import React from "react";

import classNames from "classnames";
import styles from "../styles/components/distribution-table.module.scss";
import tableStyles from "../styles/components/table.module.scss";

const formatCell = (value: number | null) => {
  if (value === null) {
    return '';
  }
  if (value === 0 ) {
    return '';
  }
  return `${value <= 0.01 ? `< 0.01` : numeral(value).format('0.00')}`
}


const getCellStyle = (value: number, min: number, max: number): React.CSSProperties => {
  const normalize = (value - min) / (max - min);
  const gradientColors: [number, string][] = [
    [0, 'rgb(226, 246, 252)'],
    [1, 'rgb(37, 172, 205)'],
  ];

  const getGradientColor = (normalized: number): string => {
    for (let i = 0; i < gradientColors.length - 1; i++) {
      const [start, startColor] = gradientColors[i];
      const [end, endColor] = gradientColors[i + 1];
      if (normalized >= start && normalized <= end) {
        const ratio = (normalized - start) / (end - start);
        const interpolate = (startValue: number, endValue: number): number =>
          Math.round(startValue + ratio * (endValue - startValue));
        const [r1, g1, b1] = startColor.match(/\d+/g)!.map(Number);
        const [r2, g2, b2] = endColor.match(/\d+/g)!.map(Number);
        return `rgb(${interpolate(r1, r2)}, ${interpolate(g1, g2)}, ${interpolate(b1, b2)})`;
      }
    }
    return gradientColors[gradientColors.length - 1][1];
  };

  const backgroundColor: string = getGradientColor(normalize);
  return { backgroundColor };
};

const getCellStylesByValue = (value: number | null, min: number, max: number) => {
  if (value === null || value === 0) {
    return {};
  }
  return getCellStyle(value, min, max);

  // if (value <= 0.01) {
  //   return { backgroundColor: '#f8d7da', color: '#721c24' };
  // } else if (value <= 0.1) {
  //   return { backgroundColor: '#fff3cd', color: '#856404' };
  // } else if (value <= 0.5) {
  //   return { backgroundColor: '#d4edda', color: '#155724' };
  // } else {
  //   return { backgroundColor: '#cce5ff', color: '#004085' };
  // }
}

interface DistributionTableProps {
  plotData: number[][]
  xAxis: (string | number)[]
  yAxis: (string | number)[]
  columnTotals: (string | number)[]
  rowsTotals: (string | number)[]
  title?: string
  xAxisTitle?: string
  yAxisTitle?: string
  min: number
  max: number
  xAxisEdges: boolean | null
  yAxisEdges: boolean | null
  xAxisDirectional: boolean | null
  yAxisDirectional: boolean | null
}

export const DistributionTable = ({
  plotData,
  xAxis,
  yAxis,
  columnTotals,
  rowsTotals,
  title,
  xAxisTitle = "X Axis",
  yAxisTitle = "Y Axis",
  min,
  max,
  xAxisEdges,
  yAxisEdges,
  xAxisDirectional,
  yAxisDirectional,
}: DistributionTableProps) => {

  return (
    <>
    <div className={styles.tableWrapper}>
      <h5 className={styles.yAxisTitle}>{yAxisTitle}</h5>
      <table className={classNames(styles.table, tableStyles.table, tableStyles.compact)}>
        <thead>
          <tr>
            <th className={tableStyles.rowValue}>Total</th>
            {columnTotals.map((col, index) => {
              if (typeof col === 'number') {
                col = numeral(col).format('0,0.00');
                return (
                  <th key={`col-${index}`} className={classNames(tableStyles.centered, {
                    [tableStyles.borderRight]: index < columnTotals.length - 1,
                  })}>
                    {col}
                  </th>
                );
              }
              return (<th key={`col-${index}`}></th>);
            })}
            <th className={tableStyles.rowValue}>100</th>
          </tr>
        </thead>
        <tbody>
          {plotData.map((row, rowIndex) => {
            return (
              <tr key={`row-${rowIndex}`}>
                <th className={classNames(tableStyles.rowValue, {
                  [styles.offsetValueY]: yAxisEdges})
                }>
                  <span>{yAxis[yAxisEdges ? rowIndex +1 : rowIndex]}{yAxisDirectional ? `°` : ``}</span>
                </th>
                {row.map((value, colIndex) => {
                  return (
                    <td key={`cell-${rowIndex}-${colIndex}`} 
                      className={classNames(tableStyles.centered, {
                        [tableStyles.borderRight]: colIndex < columnTotals.length - 1,
                      })}
                      style={getCellStylesByValue(value, min, max) as React.CSSProperties}
                    >
                      {formatCell(value)}
                    </td>
                  );
                })}
                <th className={tableStyles.rowValue}>{numeral(rowsTotals[rowIndex]).format('0.00')}</th>
              </tr>
            );
          })
          }
        </tbody>
        <tfoot>
          <tr>
            <th className={classNames(tableStyles.borderRight, {
              [tableStyles.centered]: xAxisEdges,
              // [tableStyles.nodata]: !xAxisEdges,
              [styles.offsetValueX]: xAxisEdges
            })}><span>{xAxisEdges ? xAxis[0] : ''}</span></th>
            {xAxis.map((col, index) => {
              if (xAxisEdges) {
                if (index === 0) {
                  return null
                } else if (index === xAxis.length - 1) {
                  return <th><b /></th>
                }
                
              }
              if (col !== null) {
                return (
                  <th 
                    key={`footer-col-${index}`} 
                    className={classNames(tableStyles.borderRight, tableStyles.centered, {
                      [styles.offsetValueX]: xAxisEdges
                    })}
                  >
                    <b />
                    <span>{col}{xAxisDirectional ? `°` : ``}</span>
                  </th>
                );
              }
              return (<th key={`footer-col-${index}`}></th>);
            })}
            <th className={classNames(tableStyles.rowValue)}><b />Total</th>
          </tr>
        </tfoot>
      </table>
    </div>
    <h5 className={styles.xAxisTitle}>{xAxisTitle}</h5>
    </>
  );
};
